import React from 'react';
import { Loader } from '@mantine/core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowLeft,
  faChevronDown,
  faChevronLeft,
  faChevronRight,
  faMoon,
  faSun,
} from '@fortawesome/free-solid-svg-icons';
import type { IconProp } from '@fortawesome/fontawesome-svg-core';
import { Button } from '../../../../components/Button';
import { asAmount } from '../../shared/utils/dashboard-page.utils';
import classes from './dashboard-header.module.scss';

export type DashboardHeaderProps = {
  headerDateText: string;
  setIsFilterDialogOpen: (open: boolean) => void;
  onStepDay: (delta: number) => void;
  canStepToNextDay: boolean;
  anchorMonthSalesExTips: number | null;
  anchorMonthLabel: string;
  anchorWeekSalesExTips: number | null;
  anchorWeekLabel: string;
  loading: boolean;
  isLightMode: boolean;
  onToggleThemeMode: () => void;
  onOpenAssistant?: () => void;
  onBackToHome: () => void;
};

export const DashboardHeader: React.FC<DashboardHeaderProps> = ({
  headerDateText,
  setIsFilterDialogOpen,
  onStepDay,
  canStepToNextDay,
  anchorMonthSalesExTips,
  anchorMonthLabel,
  anchorWeekSalesExTips,
  anchorWeekLabel,
  loading,
  isLightMode,
  onToggleThemeMode,
  onOpenAssistant,
  onBackToHome,
}) => {
  const salesRows = [
    { key: 'month', label: 'החודש', subLabel: anchorMonthLabel, amount: anchorMonthSalesExTips },
    { key: 'week', label: 'השבוע', subLabel: anchorWeekLabel, amount: anchorWeekSalesExTips },
  ];

  return (
    <section className={[classes.hero, isLightMode ? classes.heroLight : ''].filter(Boolean).join(' ')}>
      <div className={classes.actions}>
        <Button
          variant="icon"
          className={`btn-bordered ${classes.askAiButton}`}
          ariaLabel="Ask AI"
          dataTestId="dashboard-header-ask-ai"
          onClick={onOpenAssistant}
          disabled={!onOpenAssistant}
        >
          <span>Ask AI</span>
          <svg className={classes.askAiIcon} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
            <path d="M12 0Q13.5 10.5 24 12Q13.5 13.5 12 24Q10.5 13.5 0 12Q10.5 10.5 12 0Z" />
          </svg>
        </Button>
        <Button
          variant="icon"
          className={`btn-bordered ${classes.control} ${classes.themeToggle}`}
          onClick={onToggleThemeMode}
          ariaLabel={isLightMode ? 'Switch to dark mode' : 'Switch to light mode'}
          dataTestId="dashboard-header-theme-toggle"
        >
          <span className={isLightMode ? classes.themeOptionActive : classes.themeOption}>
            <FontAwesomeIcon icon={faSun as IconProp} aria-hidden="true" />
          </span>
          <span className={isLightMode ? classes.themeOption : classes.themeOptionActive}>
            <FontAwesomeIcon icon={faMoon as IconProp} aria-hidden="true" />
          </span>
        </Button>
        {loading ? <Loader size="sm" /> : null}
      </div>

      <div className={classes.dateNav}>
        <Button
          variant="icon"
          className={classes.dayArrow}
          onClick={() => onStepDay(-1)}
          ariaLabel="היום הקודם"
          dataTestId="dashboard-header-prev-day"
        >
          <FontAwesomeIcon icon={faChevronRight as IconProp} aria-hidden="true" />
        </Button>
        <Button
          variant="icon"
          className={`btn-bordered ${classes.control} ${classes.dateButton}`}
          onClick={() => setIsFilterDialogOpen(true)}
          ariaLabel="בחירת פילטר תאריך"
          dataTestId="dashboard-header-date"
        >
          <svg className={classes.dateIcon} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <rect x="3" y="4.5" width="18" height="16" rx="3" />
            <line x1="3" y1="9" x2="21" y2="9" />
            <line x1="8" y1="2.5" x2="8" y2="6" />
            <line x1="16" y1="2.5" x2="16" y2="6" />
          </svg>
          <h1 className={classes.dateText}>{headerDateText}</h1>
          <FontAwesomeIcon className={classes.dateIcon} icon={faChevronDown as IconProp} aria-hidden="true" />
        </Button>
        <Button
          variant="icon"
          className={classes.dayArrow}
          onClick={() => onStepDay(1)}
          disabled={!canStepToNextDay}
          ariaLabel="היום הבא"
          dataTestId="dashboard-header-next-day"
        >
          <FontAwesomeIcon icon={faChevronLeft as IconProp} aria-hidden="true" />
        </Button>
      </div>

      <Button
        variant="icon"
        className={`btn-bordered ${classes.control} ${classes.backButton}`}
        onClick={onBackToHome}
        ariaLabel="חזרה לדף הבית"
        dataTestId="dashboard-header-back"
      >
        <FontAwesomeIcon icon={faArrowLeft as IconProp} aria-hidden="true" />
      </Button>

      <div className={classes.sales}>
        <p className={classes.salesCaption}>מכירות, לא כולל טיפים</p>
        <div className={classes.salesCard}>
          {salesRows.map((row) => (
            <div key={row.key} className={classes.salesRow} data-testid={`dashboard-header-${row.key}-sales`}>
              <span className={classes.salesLabels}>
                <span className={classes.salesLabel}>{row.label}</span>
                <span className={`${classes.salesSubLabel} ${row.key === 'week' ? classes.weekSubLabel : ''}`}>
                  {row.subLabel}
                </span>
              </span>
              <span className={classes.salesAmount}>
                {row.amount === null ? '\u2014' : `₪${asAmount(row.amount)}`}
              </span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};
